import { useCallback, useEffect, useMemo, useState } from 'react';
import useExpandCollapseAllState from 'modules/common/hooks/useExpandCollapseAllState';
import { SmartDiaryEntry } from '../../type';
const BATCH_SIZE = 10;

const makeEntriesByDay = (entries: SmartDiaryEntry[]) =>
  entries.reduce((acc, entry) => {
    acc[entry.date] = acc[entry.date] ?? [];
    acc[entry.date].push(entry);
    return acc;
  }, {} as Record<string, SmartDiaryEntry[]>);

export const useSmartDiaryPagination = (
  smartEntries: SmartDiaryEntry[],
  expandCollapseAllId: string
): {
  hasMore: boolean;
  entriesByDay: Record<string, SmartDiaryEntry[]>;
  loadMore: () => void;
} => {
  const { expandAllState } = useExpandCollapseAllState(expandCollapseAllId);
  const [index, setIndex] = useState(1);
  const [hasMore, setHasMore] = useState(
    expandAllState ? smartEntries.length > BATCH_SIZE : false
  );

  const initialData = useMemo(() => {
    const entries = expandAllState
      ? smartEntries.slice(0, BATCH_SIZE)
      : smartEntries;
    return makeEntriesByDay(entries);
  }, [expandAllState, smartEntries]);
  const [entriesByDay, setEntriesByDay] = useState<
    Record<string, SmartDiaryEntry[]>
  >(initialData);

  useEffect(() => {
    setIndex(1);
    setHasMore(expandAllState ? smartEntries.length > BATCH_SIZE : false);
    setEntriesByDay(initialData);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [smartEntries]);

  const loadMore = useCallback(() => {
    const newIndex = expandAllState
      ? index + 1
      : Math.ceil(smartEntries.length / BATCH_SIZE);
    const entries = smartEntries.slice(0, BATCH_SIZE * newIndex);
    setEntriesByDay(makeEntriesByDay(entries));
    setHasMore(smartEntries.length > newIndex * BATCH_SIZE);
    setIndex(newIndex);
  }, [index, smartEntries, expandAllState]);
  return { hasMore, entriesByDay, loadMore };
};
